// @flow
import { formActions } from 'redux-pure-form';
import api, { sleep } from '../../configs/api-generators.js';
import { setProgress, setMessages, setModal } from '../ui/ui.actions.js';
import Organization from '../organization/organization.model.js';

import omit from '../utils/omit.js';

import {
  ADD_INVOICE,
  SAVE_INVOICE,
  DELETE_INVOICE,
  GET_TOTAL,
  GET_INVOICES,
  PAY_INVOICE
} from './invoice.const.js';

import Invoice from './invoice.model.js';
import { invoiceSelector, invoicePaySelector } from './invoice.selectors.js';
import { eventSelector } from '../event/event.selectors.js';
import { payerSelector } from '../user/user.selectors.js';
import {
  organizationByEventSelector,
  organizationSelector,
  organizationsSelector
} from '../organization/organization.selectors.js';

import { extractMessages } from '../ui/ui.helpers.js';

export function* addInvoice(): Yield<*> {
  try {
    yield setProgress(true, ADD_INVOICE);

    const state = yield s => s;
    const invoice = invoiceSelector(state);

    const event = eventSelector(state);
    const orgEvent = organizationByEventSelector(state);

    const org = organizationSelector(state);
    const payer = payerSelector(state);

    const url = event.id
      ? `/org/${orgEvent.unique}/invoices/add/event/${event.id}/`
      : `/org/${org.unique}/invoices/add/user/${payer.id}/`;

    const response = yield* api(url, {
      number: invoice.number,
      userCreateTimestamp: invoice.userCreateTimestamp,
      resources: invoice.resources,
      products: invoice.products
    });

    if (response.ok) {
      yield {
        type: ADD_INVOICE,
        payload: { invoice: response.data.invoice }
      };

      yield setMessages(extractMessages(response));
      yield sleep(500);

      window.location = Invoice.getListLink(
        event.pilotID || payer.id,
        orgEvent.unique || org.unique
      );
      return setProgress(false, ADD_INVOICE);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, ADD_INVOICE);
  }
}

export function* saveInvoice(): Yield<*> {
  try {
    yield setProgress(true, SAVE_INVOICE);

    const state = yield s => s;
    const invoice = invoiceSelector(state);

    const event = eventSelector(state);
    const orgEvent = organizationByEventSelector(state);

    const org = organizationSelector(state);
    const payer = payerSelector(state);

    const response = yield* api(
      `/org/${orgEvent.unique || org.unique}/invoices/save/${invoice.id}/`,
      {
        number: invoice.number,
        resources: invoice.resources,
        userCreateTimestamp: invoice.userCreateTimestamp,
        products: invoice.products
      }
    );

    if (response.ok) {
      yield {
        type: SAVE_INVOICE,
        payload: { invoice: response.data.invoice }
      };

      yield setMessages(extractMessages(response));
      yield sleep(500);

      window.location = Invoice.getListLink(
        event.pilotID || payer.id,
        orgEvent.unique || org.unique
      );
      return setProgress(false, SAVE_INVOICE);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, SAVE_INVOICE);
  }
}

export function* deleteInvoice(): Yield<*> {
  try {
    yield setProgress(true, DELETE_INVOICE);

    const state = yield s => s;
    const event = eventSelector(state);

    const invoice = invoiceSelector(state);
    const orgEvent = organizationByEventSelector(state);

    const org = organizationSelector(state);
    const payer = payerSelector(state);

    const response = yield* api(
      `/org/${orgEvent.unique || org.unique}/invoices/delete/${invoice.id}/`,
      {}
    );

    if (response.ok) {
      yield {
        type: DELETE_INVOICE,
        payload: { id: invoice.id }
      };

      yield setMessages(extractMessages(response));
      yield sleep(500);

      window.location = Invoice.getListLink(
        event.pilotID || payer.id,
        orgEvent.unique || org.unique
      );
      return setProgress(false, DELETE_INVOICE);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, DELETE_INVOICE);
  }
}

type Payload = {|
  id?: string,
  resources: $ElementType<$ElementType<ReduxState, 'invoice'>, 'resources'>,
  products: $ElementType<$ElementType<ReduxState, 'invoice'>, 'products'>
|};

export function* getPrice(): Yield<*> {
  try {
    yield setProgress(true, GET_TOTAL);

    const state = yield s => s;
    const invoice = invoiceSelector(state);

    const event = eventSelector(state);
    const orgEvent = organizationByEventSelector(state);

    const org = organizationSelector(state);
    const payer = payerSelector(state);

    const url = payer.id
      ? `/org/${org.unique}/invoices/getPrice/user/${payer.id}/`
      : `/org/${orgEvent.unique}/invoices/getPrice/event/${event.id}/`;

    const payload: Payload = {
      number: invoice.number,
      resources: invoice.resources,
      products: invoice.products
    };

    if (invoice.id) payload.id = invoice.id;

    const response = yield* api(url, payload);

    if (response.ok) {
      yield {
        type: GET_TOTAL,
        payload: { invoice: response.data.invoice }
      };

      yield setMessages(extractMessages(response));
      return setProgress(false, GET_TOTAL);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, GET_TOTAL);
  }
}

export function* deleteProduct(id: number): Yield<*> {
  try {
    const state = yield s => s;
    const invoice = invoiceSelector(state);

    yield formActions.onChange({
      invoice: {
        ...invoice,
        products: omit(invoice.products, id.toString())
      }
    });

    return yield* getPrice();
  } catch (e) {
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* deleteResource(id: number): Yield<*> {
  try {
    const state = yield s => s;
    const invoice = invoiceSelector(state);

    yield formActions.onChange({
      invoice: {
        ...invoice,
        resources: omit(invoice.resources, id.toString())
      }
    });

    return yield* getPrice();
  } catch (e) {
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* deleteCounter(id: number, resourceId: number): Yield<*> {
  try {
    const state = yield s => s;
    const invoice = invoiceSelector(state);

    yield formActions.onChange({
      invoice: {
        ...invoice,
        resources: {
          ...invoice.resources,
          [resourceId]: {
            ...invoice.resources[resourceId],
            counters: {
              ...invoice.resources[resourceId].counters,
              [id]: {
                ...invoice.resources[resourceId].counters[id],
                enableRate: '0'
              }
            }
          }
        }
      }
    });

    return yield* getPrice();
  } catch (e) {
    return setMessages({
      type: 'error',
      text: e.message
    });
  }
}

export function* getInvoices(filterParams: string): Yield<*> {
  try {
    yield setProgress(true, GET_INVOICES);

    const state = yield s => s;
    const payer = payerSelector(state);
    const org = organizationSelector(state);

    const response = yield* api(
      Organization.orgAccess
        ? `/org/${org.unique}/invoices/getAll/${payer.id}/${filterParams}/`
        : `/invoices/getAll/${filterParams}/`,
      {}
    );

    if (response.ok) {
      yield {
        type: GET_INVOICES,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      return setProgress(false, GET_INVOICES);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, GET_INVOICES);
  }
}

export function* payInvoice(): Yield<*> {
  try {
    yield setProgress(true, PAY_INVOICE);

    const state = yield s => s;
    const URL = state.URL;
    const invoicePay = invoicePaySelector(state);
    const organization = organizationSelector(state);
    const uri = URL.org
      ? `/org/${organization.unique}/invoices/createPayment/${invoicePay.organizationId}/`
      : `/invoices/createPayment/${invoicePay.organizationId}/`;
    const response = yield* api(uri, {
      [invoicePay.paymantMethod.type]: invoicePay.paymantMethod.value,
      invoices: invoicePay.checkedInvoices.reduce(
        (acc, invoiceId) => ({
          ...acc,
          [invoiceId]: invoicePay.invoices[invoiceId]
        }),
        {}
      ),
      transactionAmount: invoicePay.checkedInvoices.reduce(
        (acc, value) => acc + parseFloat(invoicePay.invoices[value]) || 0,
        0
      )
    });

    if (response.ok) {
      yield setMessages(extractMessages(response));

      window.location = Organization.orgAccess
        ? Invoice.getListLink(invoicePay.organizationId, organization.unique)
        : Invoice.getListLink('/invoices/');

      return setProgress(false, PAY_INVOICE);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, PAY_INVOICE);
  }
}
